<template>
  <div class="rtp-widget">
    <!-- Header -->
    <div class="widget-header">
      <div>
        <h2 class="widget-title">{{ $t('admin.rtp_monitoring.widget_title') }}</h2>
        <p class="widget-subtitle">{{ $t('admin.rtp_monitoring.widget_subtitle') }}</p>
      </div>
      <div class="header-controls">
        <div class="threshold-control">
          <label class="threshold-label">
            {{ $t('admin.rtp_monitoring.alert_threshold') }}
            <input
              v-model.number="threshold"
              type="number"
              min="0.1"
              max="20"
              step="0.1"
              class="threshold-input"
            />
            <span class="threshold-unit">%</span>
          </label>
        </div>
        <div class="period-tabs">
          <button
            v-for="p in periods"
            :key="p.value"
            class="period-tab"
            :class="{ active: period === p.value }"
            type="button"
            @click="period = p.value"
          >
            {{ p.label }}
          </button>
        </div>
        <button class="btn btn-secondary" type="button" :disabled="loading" @click="fetchData">
          <span v-if="loading" class="spinner" />
          <span v-else>{{ $t('admin.rtp_monitoring.refresh') }}</span>
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading && games.length === 0" class="widget-loading">
      <span class="spinner spinner-lg" />
      <span>{{ $t('admin.rtp_monitoring.loading') }}</span>
    </div>

    <!-- Empty state -->
    <div v-else-if="!loading && games.length === 0" class="widget-empty">
      {{ $t('admin.rtp_monitoring.no_data') }}
    </div>

    <!-- Table -->
    <div v-else class="table-wrap">
      <table class="rtp-table">
        <thead>
          <tr>
            <th>{{ $t('admin.rtp_monitoring.col_game') }}</th>
            <th class="num">{{ $t('admin.rtp_monitoring.col_theoretical_rtp') }}</th>
            <th class="num">{{ $t('admin.rtp_monitoring.col_observed_rtp') }}</th>
            <th class="num">{{ $t('admin.rtp_monitoring.col_deviation') }}</th>
            <th class="num">{{ $t('admin.rtp_monitoring.col_rounds_24h') }}</th>
            <th class="num">{{ $t('admin.rtp_monitoring.col_wagered_24h') }}</th>
            <th class="num">{{ $t('admin.rtp_monitoring.col_status') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="game in games" :key="game.game_id">
            <td>
              <div class="game-name">{{ game.game_title }}</div>
              <div class="game-slug">{{ game.game_slug }}</div>
            </td>
            <td class="num mono">
              {{ game.theoretical_rtp !== null ? game.theoretical_rtp.toFixed(2) + '%' : '—' }}
            </td>
            <td class="num mono" :class="rtpClass(game)">
              {{ observedRtp(game) !== null ? observedRtp(game)!.toFixed(2) + '%' : '—' }}
            </td>
            <td class="num mono" :class="deviationClass(game)">
              {{ deviationFormatted(game) }}
            </td>
            <td class="num mono">{{ game.total_rounds_24h.toLocaleString() }}</td>
            <td class="num mono">{{ formatCents(game.total_wagered_24h_cents) }}</td>
            <td class="num">
              <span class="status-badge" :class="statusClass(game)">
                {{ statusLabel(game) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Footer: last updated + auto-refresh countdown -->
    <div v-if="lastUpdated" class="widget-footer">
      <span class="last-updated">{{ $t('admin.rtp_monitoring.last_updated', { time: lastUpdated }) }}</span>
      <span class="refresh-info">{{ $t('admin.rtp_monitoring.auto_refresh') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const toast = useToastStore()

// ----------- Types -----------

interface RtpGame {
  game_id: number
  game_title: string
  game_slug: string
  theoretical_rtp: number | null
  observed_rtp_24h: number | null
  observed_rtp_7d: number | null
  observed_rtp_30d: number | null
  total_rounds_24h: number
  total_wagered_24h_cents: number
  total_won_24h_cents: number
  deviation_24h: number | null
  deviation_7d: number | null
  deviation_30d: number | null
}

// ----------- State -----------

const games = ref<RtpGame[]>([])
const loading = ref(false)
const period = ref<'24h' | '7d' | '30d'>('24h')
const threshold = ref(2)
const lastUpdated = ref('')

let refreshTimer: ReturnType<typeof setInterval> | null = null

const periods = [
  { label: '24h', value: '24h' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
] as const

// ----------- Helpers -----------

function observedRtp(game: RtpGame): number | null {
  if (period.value === '24h') return game.observed_rtp_24h
  if (period.value === '7d') return game.observed_rtp_7d
  return game.observed_rtp_30d
}

function deviation(game: RtpGame): number | null {
  if (period.value === '24h') return game.deviation_24h
  if (period.value === '7d') return game.deviation_7d
  return game.deviation_30d
}

function deviationFormatted(game: RtpGame): string {
  const dev = deviation(game)
  if (dev === null) return '—'
  const sign = dev >= 0 ? '+' : ''
  return sign + dev.toFixed(2) + '%'
}

function formatCents(cents: number): string {
  if (cents === 0) return '$0'
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function absDeviation(game: RtpGame): number | null {
  const dev = deviation(game)
  return dev !== null ? Math.abs(dev) : null
}

// ----------- CSS class helpers -----------

function deviationClass(game: RtpGame): string {
  const abs = absDeviation(game)
  if (abs === null) return 'text-muted'
  if (abs < 1) return 'text-green'
  if (abs < threshold.value) return 'text-yellow'
  return 'text-red'
}

function rtpClass(game: RtpGame): string {
  const obs = observedRtp(game)
  if (obs === null) return 'text-muted'
  return ''
}

function statusClass(game: RtpGame): string {
  const abs = absDeviation(game)
  if (abs === null) return 'badge-neutral'
  if (abs < 1) return 'badge-ok'
  if (abs < threshold.value) return 'badge-warning'
  return 'badge-alert'
}

function statusLabel(game: RtpGame): string {
  const abs = absDeviation(game)
  if (abs === null) return t('admin.rtp_monitoring.status_no_data')
  if (abs < 1) return t('admin.rtp_monitoring.status_ok')
  if (abs < threshold.value) return t('admin.rtp_monitoring.status_watch')
  return t('admin.rtp_monitoring.status_alert')
}

// ----------- Data fetching -----------

async function fetchData(): Promise<void> {
  loading.value = true
  try {
    const response = await apiClient.get<RtpGame[]>('/admin/api/rtp-monitoring')
    games.value = response.data
    const now = new Date()
    lastUpdated.value = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch {
    toast.error(t('admin.rtp_monitoring.failed_to_load'))
  } finally {
    loading.value = false
  }
}

// ----------- Lifecycle -----------

onMounted(async () => {
  await fetchData()
  refreshTimer = setInterval(fetchData, 60000)
})

onBeforeUnmount(() => {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
})
</script>

<style scoped>
.rtp-widget {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

/* Header */
.widget-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  flex-wrap: wrap;
}

.widget-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.widget-subtitle {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* Threshold */
.threshold-control {
  display: flex;
  align-items: center;
}

.threshold-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

.threshold-input {
  width: 56px;
  padding: 5px 8px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 12px;
  font-family: inherit;
  text-align: center;
}

.threshold-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.threshold-unit {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Period tabs */
.period-tabs {
  display: flex;
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.period-tab {
  padding: 5px 12px;
  border-radius: 6px;
  border: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  transition: all 150ms ease;
}

.period-tab:hover {
  color: var(--text-primary, #fff);
}

.period-tab.active {
  background: var(--bg-secondary, #1a2c38);
  color: var(--text-primary, #fff);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

/* Button */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

/* Loading / empty */
.widget-loading,
.widget-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

/* Table */
.table-wrap {
  overflow-x: auto;
}

.rtp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.rtp-table th {
  padding: 10px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
}

.rtp-table th.num {
  text-align: right;
}

.rtp-table td {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: var(--text-secondary, #b1bad3);
  vertical-align: middle;
}

.rtp-table td.num {
  text-align: right;
}

.rtp-table tbody tr:last-child td {
  border-bottom: none;
}

.rtp-table tbody tr:hover td {
  background: rgba(255, 255, 255, 0.02);
}

/* Game name cell */
.game-name {
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin-bottom: 2px;
}

.game-slug {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
}

/* Monospace numbers */
.mono {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
}

/* Color classes */
.text-green {
  color: #00e701;
}

.text-yellow {
  color: #fbbf24;
}

.text-red {
  color: #f87171;
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4);
}

/* Status badges */
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge-ok {
  background: rgba(0, 231, 1, 0.12);
  color: #00e701;
}

.badge-warning {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
}

.badge-alert {
  background: rgba(248, 113, 113, 0.15);
  color: #f87171;
}

.badge-neutral {
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-tertiary, #7f8fa4);
}

/* Footer */
.widget-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
}

.spinner-lg {
  width: 24px;
  height: 24px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 900px) {
  .widget-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
